import {
  CompanyChangeSpecialPurpose
} from '../../forms/asic-forms/company/company-change-special-purpose/company-change-special-purpose.model';
import { Guid } from "@shared/utils/guid.helper";
import { EntityChangeData } from "./entity-change-data";
import { AsicStatus } from "./asic-status.enum";
import { DocumentStatusEnum } from "./document-status.enum";
import { DocumentationType } from "./document-enums";
import { TrustChangeRelationship } from "../trusts/trust-change-relationship";
import {
  TrustDistribution
} from "../../forms/asic-forms/trusts/form-distribution-resolution-or-minutes/form-distribution-resolution-or-minutes.model";
import { DocumentBase } from '@models/documents/document-base';
import { EdgeValidationResult } from "../edge/edge-validation-result";
import { TrustDiscretionaryEstablishment } from "../../forms/models/trusts/trust-discretionary-establishment.model";
import { TrustUnitEstablishment } from "../../forms/models/trusts/trust-unit-establishment.model";
import {
  CompanySecurityCancellation,
  CompanySecurityConsolidationSubdivision,
  CompanySecurityIssue,
  CompanySecurityTransfer
} from "../../forms/models/shares";
import {
  AsicFormRA01,
  AsicFormRA04,
  CompanyChangeAddress,
  CompanyChangeDeregister,
  CompanyChangeHoldingCompany,
  CompanyChangeName,
  CompanyChangeOfficer,
  CompanyChangeOfficerResignation,
  CompanyChangePartyName,
  CompanyChangeType,
  CompanyDividendStatement,
  CompanyForm280,
  CompanyForm281,
  CompanyForm361,
  CompanyForm362,
  CompanyForm388,
  CompanyMistakeCorrection,
  CompanyRegistration,
  CompanyWithdrawLodgedDocumentChange,
  Form410BTransaction,
  Form410FTransaction
} from "../../forms/models/company";

export class Document extends DocumentBase {
  documentStatus?: DocumentStatusEnum;
  documentationType?: DocumentationType;
  asicStatus: AsicStatus;
  asicDocumentNumber?: string | null;
  asicTraceNumber?: string;
  edgeValidationResult?: EdgeValidationResult | null;
  lodgedOn: Date | null;
  lodgementDeadline: Date | null;
  earliestChangeDate: string | null;

  constructor(data: Partial<Document> = {}) {
    super(data);
    this.changes = data.changes?.map(x => this.createChangeDataInstance(x)) ?? [];
    this.documentStatus = data.documentStatus;
    this.documentationType = data.documentationType ?? DocumentationType.Default;
    this.asicStatus = data.asicStatus ?? AsicStatus.NotLodged;
    this.asicDocumentNumber = data.asicDocumentNumber ?? null;
    this.asicTraceNumber = data.asicTraceNumber ?? '';
    this.edgeValidationResult = data.edgeValidationResult ?? null;
    this.lodgedOn = data.lodgedOn ?? null;
    this.lodgementDeadline = data.lodgementDeadline ? new Date(data.lodgementDeadline) : null;
    this.earliestChangeDate = data.earliestChangeDate ?? null;
  }

  createChangeDataInstance(data): EntityChangeData {
    //workaround for type casting
    switch (data.$type) {
      case CompanyChangeAddress.$type:
        return new CompanyChangeAddress(data as Partial<CompanyChangeAddress>);
      case CompanyChangeOfficer.$type:
        return new CompanyChangeOfficer(data as Partial<CompanyChangeOfficer>);
      case CompanyForm362.$type:
        return new CompanyForm362(data as Partial<CompanyForm362>);
      case CompanyChangeHoldingCompany.$type:
        return new CompanyChangeHoldingCompany(data as Partial<CompanyChangeHoldingCompany>);
      case CompanyChangeSpecialPurpose.$type:
        return new CompanyChangeSpecialPurpose(data as Partial<CompanyChangeSpecialPurpose>);
      case CompanyMistakeCorrection.$type:
        return new CompanyMistakeCorrection(data as Partial<CompanyMistakeCorrection>);
      case CompanyChangeOfficerResignation.$type:
        return new CompanyChangeOfficerResignation(data as Partial<CompanyChangeOfficerResignation>);
      case CompanyChangePartyName.$type:
        return new CompanyChangePartyName(data as Partial<CompanyChangePartyName>);
      case Form410BTransaction.$type:
        return new Form410BTransaction(data as Partial<Form410BTransaction>);
      case Form410FTransaction.$type:
        return new Form410FTransaction(data as Partial<Form410FTransaction>);
      case CompanyForm361.$type:
        return new CompanyForm361(data as Partial<CompanyForm361>);
      case CompanyWithdrawLodgedDocumentChange.$type:
        return new CompanyWithdrawLodgedDocumentChange(data as Partial<CompanyWithdrawLodgedDocumentChange>);
      case CompanyChangeDeregister.$type:
        return new CompanyChangeDeregister(data as Partial<CompanyChangeDeregister>);
      case CompanyDividendStatement.$type:
        return new CompanyDividendStatement(data as Partial<CompanyDividendStatement>);
      case CompanyChangeName.$type:
        return new CompanyChangeName(data as Partial<CompanyChangeName>);
      case CompanyChangeType.$type:
        return new CompanyChangeType(data as Partial<CompanyChangeType>);
      case CompanyForm280.$type:
        return new CompanyForm280(data as Partial<CompanyForm280>);
      case CompanyForm388.$type:
        return new CompanyForm388(data as Partial<CompanyForm388>);
      case CompanyForm281.$type:
        return new CompanyForm281(data as Partial<CompanyForm281>);
      case AsicFormRA01.$type:
        return new AsicFormRA01(data as Partial<AsicFormRA01>);
      case AsicFormRA04.$type:
        return new AsicFormRA04(data as Partial<AsicFormRA04>);
      case CompanySecurityCancellation.$type:
        return new CompanySecurityCancellation(data as Partial<CompanySecurityCancellation>);
      case CompanySecurityIssue.$type:
        return new CompanySecurityIssue(data as Partial<CompanySecurityIssue>);
      case CompanySecurityTransfer.$type:
        return new CompanySecurityTransfer(data as Partial<CompanySecurityTransfer>);
      case CompanySecurityConsolidationSubdivision.$type:
        return new CompanySecurityConsolidationSubdivision(data as Partial<CompanySecurityConsolidationSubdivision>);
      case CompanyRegistration.$type:
        return new CompanyRegistration(data as Partial<CompanyRegistration>);
      case TrustChangeRelationship.$type:
        return new TrustChangeRelationship(data as Partial<TrustChangeRelationship>);
      case TrustDistribution.$type:
        return new TrustDistribution(data as Partial<TrustDistribution>);
      case TrustDiscretionaryEstablishment.$type:
        return new TrustDiscretionaryEstablishment(data as Partial<TrustDiscretionaryEstablishment>);
      case TrustUnitEstablishment.$type:
        return new TrustUnitEstablishment(data as Partial<TrustUnitEstablishment>);
      default:
        throw new Error(`Unknown change data type: ${ data.$type }`);
    }
  }

  static prepareToRequest(document: Document): Document {
    const preparedDocument = {
      ...document,
      changes: document.changes.map((change) => change.prepareToRequest()),
      entityId: document.entityId === Guid.EmptyGuid ? null : document.entityId,
      company: null
    } as any as Document;

    delete preparedDocument.createdOn;
    delete preparedDocument.modifiedOn;
    delete (preparedDocument as { lodgementDeadline: any }).lodgementDeadline;

    return preparedDocument;
  }

  static isImportedDocument(document: Partial<Document>): boolean {
    return document.type == 'Imported';
  }

  static isDocumentInvoice(document: Partial<Document>): boolean {
    return document.type == 'RC09';
  }
}
